import Swiper from 'swiper';
import { resolveVariationGallery } from './product-gallery-data.mjs';

const TRANSITION_DURATION = 360;
const emptyGallery = { showVariation() {}, select() {}, getItems: () => [], getIndex: () => 0 };

/** Gallery state is shared by the product hero and its full-screen viewer. */
export const createProductGallery = (root, labels) => {
    if (!root) return emptyGallery;
    let slides = [...root.querySelectorAll('[data-gallery-slide]')];
    let thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const stage = root.querySelector('.ProductGallery__stage');
    const viewport = root.querySelector('[data-gallery-viewport]');
    if (!slides.length || !stage || !viewport) return emptyGallery;
    const zoom = root.querySelector('[data-gallery-zoom]');
    const caption = root.querySelector('[data-gallery-caption]');
    const count = root.querySelector('[data-gallery-count]');
    const controls = root.querySelector('[data-gallery-controls]');
    const track = slides[0].parentElement;
    const thumbnails = thumbs[0].parentElement;
    const originalSlides = slides;
    const originalThumbs = thumbs;
    const variantSlide = slides[0].cloneNode(true);
    const variantThumb = thumbs[0].cloneNode(true);
    variantSlide.querySelector('img').removeAttribute('fetchpriority');
    variantSlide.querySelector('img').loading = 'eager';
    let current = 0;
    const initial = slides.map((slide, index) => {
        const image = slide.querySelector('img');
        return {
            id: Number(slide.dataset.imageId), src: slide.href,
            width: Number(slide.dataset.fullWidth), height: Number(slide.dataset.fullHeight),
            previewSrc: image.src, previewSrcset: image.getAttribute('srcset') || '',
            msrc: thumbs[index].querySelector('img').src,
            alt: image.alt, caption: slide.dataset.caption || '',
        };
    });
    let items = initial;
    const photoLabel = (index) => labels.photo.replace('{current}', index + 1).replace('{total}', slides.length);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = (swiper) => {
        current = Math.min(swiper.activeIndex, slides.length - 1);
        slides.forEach((slide, i) => {
            slide.toggleAttribute('inert', i !== current);
            slide.setAttribute('aria-hidden', String(i !== current));
        });
        thumbs.forEach((thumb, i) => {
            thumb.setAttribute('aria-label', photoLabel(i));
            if (i === current) thumb.setAttribute('aria-current', 'true');
            else thumb.removeAttribute('aria-current');
        });
        zoom.href = slides[current].href;
        caption.textContent = slides[current].dataset.caption;
        count.textContent = `${current + 1} / ${slides.length}`;
        count.setAttribute('aria-label', photoLabel(current));
        if (controls) controls.hidden = slides.length < 2;
    };
    // Keep every slide in the track: the image follows the pointer throughout a drag.
    // The server-rendered hidden attributes remain a useful no-JavaScript fallback.
    slides.forEach((slide) => { slide.hidden = false; });
    const slider = new Swiper(viewport, {
        slidesPerView: 1,
        speed: reducedMotion.matches ? 0 : TRANSITION_DURATION,
        rewind: true,
        watchOverflow: true,
        allowTouchMove: slides.length > 1,
        simulateTouch: true,
        grabCursor: slides.length > 1,
        threshold: 6,
        touchAngle: 40,
        longSwipesRatio: 0.2,
        touchStartPreventDefault: false,
        preventClicks: true,
        preventClicksPropagation: true,
        lazyPreloadPrevNext: 1,
        on: { init: sync, slideChange: sync },
    });
    const show = (index, speed = slider.params.speed) => {
        slider.slideTo((index + slides.length) % slides.length, speed);
        sync(slider); // A different variant can occupy the same second position.
    };
    const updateMotion = ({ matches }) => { slider.params.speed = matches ? 0 : TRANSITION_DURATION; };
    reducedMotion.addEventListener('change', updateMotion);
    slider.on('destroy', () => reducedMotion.removeEventListener('change', updateMotion));
    thumbnails.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (!thumb) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); show(Number(thumb.dataset.galleryThumb));
    });
    root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => show(current - 1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => show(current + 1));
    stage.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
        event.preventDefault(); show(current + (event.key === 'ArrowRight' ? 1 : -1));
        slides[current].focus({ preventScroll: true });
    });
    return {
        getItems: () => items,
        getIndex: () => current,
        select: (index) => show(index, 0),
        showVariation(variation) {
            const resolved = resolveVariationGallery(initial, variation);
            items = resolved.items;
            const entries = items.map((image) => {
                const originalIndex = initial.indexOf(image);
                if (originalIndex >= 0) return { slide: originalSlides[originalIndex], thumb: originalThumbs[originalIndex] };
                const preview = variantSlide.querySelector('img');
                variantSlide.href = image.src;
                Object.assign(variantSlide.dataset, { imageId: image.id, fullWidth: image.width, fullHeight: image.height, caption: image.caption });
                preview.srcset = image.previewSrcset;
                preview.src = image.previewSrc;
                preview.alt = image.alt;
                variantThumb.href = image.src;
                variantThumb.querySelector('img').src = image.msrc;
                return { slide: variantSlide, thumb: variantThumb };
            });
            slides = entries.map(({ slide }, index) => {
                slide.dataset.gallerySlide = index;
                slide.hidden = false;
                return slide;
            });
            thumbs = entries.map(({ thumb }, index) => {
                thumb.dataset.galleryThumb = index;
                return thumb;
            });
            track.replaceChildren(...slides);
            thumbnails.replaceChildren(...thumbs);
            slider.unsetGrabCursor();
            slider.allowTouchMove = slider.params.allowTouchMove = slides.length > 1;
            slider.params.grabCursor = slides.length > 1;
            slider.update();
            if (slider.params.grabCursor) slider.setGrabCursor();
            show(resolved.index, 0);
        },
    };
};
